import React, { useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { EditorView } from '@codemirror/view';
import * as CodeFormatter from './CodeFormatter';
import { Maximize2, Minimize2 } from 'lucide-react';

// Base extensions for CodeMirror editor without language
const getCodeMirrorExtensions = (language: 'javascript' | 'json') => [
  language === 'javascript' ? javascript({ jsx: false }) : json(),
  EditorView.lineWrapping,
  EditorView.theme({
    '&': {
      fontSize: '14px',
      fontFamily: "'Menlo', 'Monaco', 'Courier New', monospace",
    },
    '.cm-content': {
      padding: '8px',
    },
    '.cm-line': {
      lineHeight: '1.6',
    },
  }),
];

interface CodeEditorProps {
  value: string;
  height: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  id?: string | undefined;
  showFormatButton?: boolean;
  onFormat?: (formattedValue: string) => void;
  language?: 'javascript' | 'json';
}

export const CodeEditor: React.FC<CodeEditorProps> = ({ 
  value, 
  height, 
  onChange, 
  readOnly = false, 
  id,
  showFormatButton = false,
  onFormat,
  language = 'javascript',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle expand/collapse toggle
  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    // Prevent body scrolling when expanded
    document.body.style.overflow = !isExpanded ? 'hidden' : '';
  };
  
  // Handle format button click
  const handleFormat = () => {
    if (!onFormat) return;
    
    try {
      const formattedValue = language === 'json' 
        ? CodeFormatter.formatJSON(value)
        : CodeFormatter.formatJavaScript(value);
        
      if (formattedValue !== value) {
        onFormat(formattedValue);
      }
    } catch (e) {
      console.error('Error during code formatting:', e);
    }
  };
  
  const extensions = getCodeMirrorExtensions(language);
  
  return (
    <div className={`code-editor-container ${isExpanded ? 'expanded' : ''}`}>
      <div className="editor-controls">
        {showFormatButton && !readOnly && (
          <button 
            className="button-subtle button-sm"
            onClick={handleFormat}
            title="Format code"
          >
            Format
          </button>
        )}
        <button 
          className="button-subtle button-icon button-sm"
          onClick={toggleExpand}
          title={isExpanded ? "Collapse editor" : "Expand editor"}
        >
          {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>
      
      <div className="editor-wrapper">
        {onChange ? (
          <CodeMirror
            id={id}
            value={value}
            height={isExpanded ? "90vh" : height}
            theme={vscodeDark}
            extensions={extensions}
            onChange={onChange}
            readOnly={readOnly}
          />
        ) : (
          <CodeMirror
            id={id}
            value={value}
            height={isExpanded ? "90vh" : height}
            theme={vscodeDark}
            extensions={extensions}
            readOnly={true}
          />
        )}
      </div>
      
      {isExpanded && (
        <div className="expanded-overlay" onClick={toggleExpand}></div>
      )}
      
      <style>{`
        .code-editor-container {
          position: relative;
          width: 100%;
          transition: all 0.3s ease;
        }
        
        .editor-controls {
          position: absolute;
          top: 5px;
          right: 5px;
          z-index: 10;
          display: flex;
          gap: 5px;
        }
        
        .expanded .editor-controls {
          position: fixed;
          top: calc(5vh + 5px);
          right: calc(5vw + 5px);
          z-index: 1001;
        }
        
        .editor-wrapper {
          position: relative;
          z-index: 1;
          transition: all 0.3s ease;
        }
        
        .expanded .editor-wrapper {
          position: fixed;
          top: 5vh;
          left: 5vw;
          width: 90vw;
          height: 90vh;
          z-index: 1000;
          box-shadow: 0 5px 30px rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          overflow: hidden;
        }
        
        .expanded-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
        }
      `}</style>
    </div>
  );
}; 